import type { MomentData } from '../../../services/momentsApi';

export const isPlainObject = (
  value: unknown
): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Keys that must not be merged via bracket assignment (prototype pollution). */
const UNSAFE_MERGE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export const isUnsafeMergeKey = (key: string): boolean =>
  UNSAFE_MERGE_KEYS.has(key);

const mergeRecords = (
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...target };

  Object.entries(source).forEach(([key, sourceValue]) => {
    if (isUnsafeMergeKey(key)) {
      return;
    }

    const targetValue = merged[key];
    if (isPlainObject(targetValue) && isPlainObject(sourceValue)) {
      merged[key] = mergeRecords(targetValue, sourceValue);
      return;
    }
    merged[key] = sourceValue;
  });

  return merged;
};

/**
 * Deep-merge incoming moment payload into existing slideout `apiData`.
 * Used after PATCH envelope or GET refresh so partial updates do not wipe nested objects.
 */
export const mergeMomentData = (
  existingData: MomentData | null,
  incomingData: Record<string, unknown>
): MomentData => {
  if (!existingData) {
    return incomingData as unknown as MomentData;
  }

  return mergeRecords(
    existingData as unknown as Record<string, unknown>,
    incomingData
  ) as unknown as MomentData;
};
